<template>
    <section class="bo-card">
        <FormTabHeader
            icon="fal fa-users"
            :title="$t('backoffice_infos.sections.raci')"
        />
        <div class="bo-body">
            <div v-for="form in forms" :key="'raci-' + form.id" class="bor-form">
                <div class="bor-form-header">
                    <div class="bor-form-title">
                        <i class="fal fa-file-alt"></i>
                        {{ form.name }}
                    </div>
                    <router-link :to="`/forms/${form.id}`" class="bo-action-btn">
                        <i class="fal fa-eye"></i>
                        {{ $t('form_types.view') }}
                    </router-link>
                </div>
                <div v-if="form.racis && form.racis.length" class="bor-roles">
                    <template v-for="role in roles">
                        <div
                            v-if="getByRole(form, role.key).length"
                            :key="role.key"
                            class="bor-role"
                        >
                            <span class="bor-role-label">{{ $t(role.labelKey) }}</span>
                            <div class="bor-badges">
                                <span
                                    v-for="u in getByRole(form, role.key)"
                                    :key="u.id"
                                    class="bor-badge"
                                    :class="`bor-badge--${role.tone}`"
                                >
                                    {{ u.name }}
                                </span>
                            </div>
                        </div>
                    </template>
                </div>
                <p v-else class="bo-empty">{{ $t('backoffice_infos.no_raci') }}</p>
            </div>
        </div>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import FormTabHeader from '@/components/Model/Form/FormComponents/FormTabHeader'

export default {
    name: 'BackofficeRaci',
    components: { FormTabHeader },
    props: {
        forms: { type: Array, required: true }
    },
    data() {
        return {
            roles: [
                { key: 'is_responsible', labelKey: 'backoffice_infos.raci.responsible', tone: 'cyan' },
                { key: 'is_accountable', labelKey: 'backoffice_infos.raci.accountable', tone: 'amber' },
                { key: 'is_consulted', labelKey: 'backoffice_infos.raci.consulted', tone: 'green' },
                { key: 'is_informed', labelKey: 'backoffice_infos.raci.informed', tone: 'purple' }
            ]
        }
    },
    methods: {
        getByRole(form, role) {
            return (form.racis || []).filter((u) => u[role])
        }
    }
}
</script>

<style scoped>
.bo-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.bo-body {
    padding: var(--v2-space-4) var(--v2-space-5);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
}

.bor-form {
    padding-bottom: var(--v2-space-4);
    border-bottom: 1px solid var(--v2-border-subtle);
}

.bor-form:last-child {
    padding-bottom: 0;
    border-bottom: none;
}

.bor-form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    margin-bottom: 12px;
}

.bor-form-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.bor-form-title i {
    color: var(--v2-primary);
    font-size: 0.85rem;
}

.bor-roles {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.bor-role {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.bor-role-label {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.bor-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.bor-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
}

.bor-badge--cyan { background: rgba(8, 145, 178, 0.12); color: #0891B2; }
.bor-badge--amber { background: rgba(245, 158, 11, 0.15); color: #b45309; }
.bor-badge--green { background: rgba(16, 185, 129, 0.12); color: #059669; }
.bor-badge--purple { background: rgba(139, 92, 246, 0.12); color: #7c3aed; }

.bo-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border-radius: var(--v2-radius-sm);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    background: transparent;
    border: 1px solid var(--v2-border);
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    flex-shrink: 0;
}

.bo-action-btn i {
    font-size: 0.72rem;
}

.bo-action-btn:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.bo-empty {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-4);
    font-style: italic;
}

@media (max-width: 768px) {
    .bo-body {
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .bor-form-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .bor-roles {
        gap: 14px;
    }
}
</style>
